/**
 * 商品タイプ「ワイン」の最少購入本数チェック
 *
 * カート内のワインの合計本数が 1 本以上かつ最少本数（既定 2 本）未満の場合、
 * 決済への遷移を止めてポップアップで案内する。
 * ワインを含まないカート、および最少本数を満たしているカートでは一切制限しない。
 *
 * 追加決済ボタン（Shop Pay 等）は iframe 経由でクリックを捕捉できないため、
 * 条件を満たさない間は非表示にし、代わりにカート内の注記で理由を案内する。
 */
(function () {
  const CHECKOUT_BUTTON_SELECTOR = '[name="checkout"]';
  /** 決済へ遷移し得るフォーム（カートページ／カートドロワー／カート通知） */
  const CHECKOUT_FORM_SELECTOR = '#cart, #CartDrawer-Form, #cart-notification-form';
  const DYNAMIC_CHECKOUT_SELECTOR = '.cart__dynamic-checkout-buttons';
  const NOTE_SELECTOR = '[data-wine-minimum-note]';
  const REMAINING_PLACEHOLDER = '[remaining]';
  const HIDDEN_CLASS = 'wine-minimum-quantity-hidden';
  const SCROLL_LOCK_CLASS = 'wine-minimum-quantity-lock';
  const FALLBACK_MINIMUM_QUANTITY = 2;

  /**
   * data 属性の値を非負整数として取得する
   * @param {string | undefined} value
   * @param {number} fallback 数値として解釈できない場合に採用する値
   * @returns {number}
   */
  function parseQuantity(value, fallback) {
    const parsed = Number.parseInt(value, 10);
    return Number.isInteger(parsed) && parsed >= 0 ? parsed : fallback;
  }

  class WineMinimumQuantity extends HTMLElement {
    constructor() {
      super();

      this.overlay = this.querySelector('[data-wine-minimum-overlay]');
      this.closeButton = this.querySelector('[data-wine-minimum-close]');
      this.remainingElement = this.querySelector('[data-wine-minimum-remaining]');

      this.productType = this.dataset.productType || '';
      this.minimumQuantity = parseQuantity(this.dataset.minimumQuantity, FALLBACK_MINIMUM_QUANTITY);
      this.wineQuantity = parseQuantity(this.dataset.wineQuantity, 0);
      this.remainingTemplate = this.dataset.remainingTemplate || '';
      this.noteTemplate = this.dataset.noteTemplate || '';

      this.previouslyFocusedElement = null;
      this.cartUpdateUnsubscriber = undefined;

      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onDocumentSubmit = this.onDocumentSubmit.bind(this);
      this.onOverlayClick = this.onOverlayClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.close = this.close.bind(this);
    }

    connectedCallback() {
      // 決済ボタンはテーマ側にも click ハンドラーがあるため、キャプチャ段階で先に判定する
      document.addEventListener('click', this.onDocumentClick, true);
      document.addEventListener('submit', this.onDocumentSubmit, true);
      this.closeButton?.addEventListener('click', this.close);
      this.overlay?.addEventListener('click', this.onOverlayClick);

      this.subscribeToCartUpdates();
      this.syncCheckoutState();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick, true);
      document.removeEventListener('submit', this.onDocumentSubmit, true);
      document.removeEventListener('keydown', this.onKeydown);
      document.documentElement.classList.remove(SCROLL_LOCK_CLASS);
      this.cartUpdateUnsubscriber?.();
    }

    /** @returns {boolean} ワインが 1 本以上あり、かつ最少本数に達していないか */
    get isBlocked() {
      return this.wineQuantity > 0 && this.wineQuantity < this.minimumQuantity;
    }

    /** @returns {number} 決済可能になるまでに追加が必要な本数 */
    get remainingQuantity() {
      return Math.max(this.minimumQuantity - this.wineQuantity, 0);
    }

    subscribeToCartUpdates() {
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        const items = event?.cartData?.items;

        // カート更新系はカート全体を返すが、商品ページからの追加は追加行のみを返すため取得し直す
        if (Array.isArray(items)) {
          this.wineQuantity = this.countWineQuantity(items);
          this.syncCheckoutState();
        } else {
          this.refreshFromCart();
        }
      });
    }

    /**
     * カート内のワインの合計本数を数える
     * @param {Array<{ product_type?: string, quantity?: number }>} items
     * @returns {number}
     */
    countWineQuantity(items) {
      if (!Array.isArray(items)) return 0;

      return items.reduce((total, item) => {
        const quantity = Number(item?.quantity) || 0;
        return item?.product_type === this.productType ? total + quantity : total;
      }, 0);
    }

    /** Ajax Cart API からカートを取得し直して状態を更新する */
    async refreshFromCart() {
      const cartUrl = window.routes?.cart_url || '/cart';

      try {
        const response = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(`カートの取得に失敗しました (status: ${response.status})`);

        const cart = await response.json();
        this.wineQuantity = this.countWineQuantity(cart.items);
        this.syncCheckoutState();
      } catch (error) {
        // 案内を出せないだけで購入自体は妨げない（誤ブロックによる離脱を避ける）
        console.error('[wine-minimum-quantity] カート情報の取得に失敗しました', error);
      }
    }

    /**
     * テンプレート内のプレースホルダーを残り本数で置き換える
     * @param {string} template
     * @param {number} remaining
     * @returns {string}
     */
    buildMessage(template, remaining) {
      return template ? template.replaceAll(REMAINING_PLACEHOLDER, String(remaining)) : '';
    }

    /** 追加決済ボタンの表示・注記・ポップアップ内の文言を現在のカート状態に合わせる */
    syncCheckoutState() {
      const { isBlocked, remainingQuantity } = this;

      document.querySelectorAll(DYNAMIC_CHECKOUT_SELECTOR).forEach((container) => {
        container.classList.toggle(HIDDEN_CLASS, isBlocked);
      });

      document.querySelectorAll(NOTE_SELECTOR).forEach((note) => {
        note.textContent = isBlocked ? this.buildMessage(this.noteTemplate, remainingQuantity) : '';
        note.hidden = !isBlocked;
      });

      if (this.remainingElement) {
        const remainingMessage = this.buildMessage(this.remainingTemplate, remainingQuantity);
        this.remainingElement.textContent = remainingMessage;
        this.remainingElement.hidden = remainingMessage === '';
      }

      if (!isBlocked) this.close();
    }

    onDocumentClick(event) {
      if (!this.isBlocked) return;

      const checkoutButton = event.target instanceof Element ? event.target.closest(CHECKOUT_BUTTON_SELECTOR) : null;
      if (!checkoutButton) return;

      this.blockCheckout(event);
    }

    onDocumentSubmit(event) {
      if (!this.isBlocked) return;

      const form = event.target instanceof Element ? event.target.closest('form') : null;
      if (!form || !form.matches(CHECKOUT_FORM_SELECTOR)) return;

      this.blockCheckout(event);
    }

    /**
     * 決済への遷移を中止してポップアップを表示する
     * @param {Event} event
     */
    blockCheckout(event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.open();
    }

    open() {
      if (!this.overlay || this.overlay.hidden === false) return;

      this.previouslyFocusedElement = document.activeElement;
      this.overlay.hidden = false;
      document.documentElement.classList.add(SCROLL_LOCK_CLASS);
      document.addEventListener('keydown', this.onKeydown);
      this.closeButton?.focus();
    }

    close() {
      if (!this.overlay || this.overlay.hidden) return;

      this.overlay.hidden = true;
      document.documentElement.classList.remove(SCROLL_LOCK_CLASS);
      document.removeEventListener('keydown', this.onKeydown);

      if (this.previouslyFocusedElement instanceof HTMLElement) {
        this.previouslyFocusedElement.focus();
      }
      this.previouslyFocusedElement = null;
    }

    onOverlayClick(event) {
      if (event.target === this.overlay) this.close();
    }

    onKeydown(event) {
      if (event.key === 'Escape') this.close();
    }
  }

  if (!customElements.get('wine-minimum-quantity')) {
    customElements.define('wine-minimum-quantity', WineMinimumQuantity);
  }
})();
